import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetContactsQuery,
  useGetDemoRequestsQuery,
  useGetSupportTicketsQuery,
} from "../store/api/apiSlice";
import { selectCurrentUser } from "../store/auth/authSlice";
import {
  getSeenEnquiryIds,
  isUnseenEnquiry,
  SEEN_ENQUIRIES_UPDATED_EVENT,
} from "../utils/seenEnquiries";

const normalizeStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

const toList = (response) => {
  if (Array.isArray(response)) return response;
  return response?.data || [];
};

const isOpenSupportTicket = (ticket) => {
  const status = normalizeStatus(ticket.status);
  return !status || status === "open";
};

const getUserId = (user) => user?._id || user?.id || user?.data?._id || null;

export const useEnquiriesCount = () => {
  const user = useSelector(selectCurrentUser);
  const userId = getUserId(user);
  const [seenVersion, setSeenVersion] = useState(0);

  const { data: contacts, isLoading: contactsLoading } = useGetContactsQuery({
    archived: false,
  });
  const { data: demos, isLoading: demosLoading } = useGetDemoRequestsQuery({
    archived: false,
  });
  const { data: supportTickets, isLoading: supportLoading } =
    useGetSupportTicketsQuery({ archived: false });

  useEffect(() => {
    const handleSeenUpdate = () => setSeenVersion((version) => version + 1);

    window.addEventListener(SEEN_ENQUIRIES_UPDATED_EVENT, handleSeenUpdate);
    return () => {
      window.removeEventListener(SEEN_ENQUIRIES_UPDATED_EVENT, handleSeenUpdate);
    };
  }, []);

  const counts = useMemo(() => {
    const seenContacts = getSeenEnquiryIds(userId, "contacts");
    const seenDemos = getSeenEnquiryIds(userId, "demos");
    const seenSupport = getSeenEnquiryIds(userId, "support");

    const contactCount = toList(contacts).filter((item) =>
      isUnseenEnquiry(item, seenContacts),
    ).length;
    const demoCount = toList(demos).filter((item) =>
      isUnseenEnquiry(item, seenDemos),
    ).length;
    const supportCount = toList(supportTickets).filter(
      (item) => isOpenSupportTicket(item) && isUnseenEnquiry(item, seenSupport),
    ).length;

    return {
      contacts: contactCount,
      demos: demoCount,
      support: supportCount,
    };
  }, [contacts, demos, supportTickets, userId, seenVersion]);

  return {
    count: counts.contacts + counts.demos + counts.support,
    counts,
    isLoading: contactsLoading || demosLoading || supportLoading,
  };
};

export default useEnquiriesCount;
